import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import CommunityLayout from "@/components/community/CommunityLayout";
import { SectionTitle } from "@/components/community/Cards";
import { cApi, errMsg, useCommunityMe, STOCK, INTERESTS, ACTIVITIES, SET_REQUIREMENT, PRIVE_SHOP_URL } from "@/lib/community";
import { startGoogleLogin } from "@/context/CustomerAuthContext";

const PILLARS = [
  ["Femininity", "We embrace softness and strength — elegance is not weakness, it's a choice."],
  ["Sisterhood", "We celebrate each other's wins, carry each other's burdens and show up."],
  ["Faith", "We are rooted in God's love and grow into the women He called us to be."],
  ["Wellness", "We care for our bodies, minds and spirits with intention."],
  ["Growth", "We pursue our purpose, our businesses and our dreams — together."],
];

const STEPS = [
  ["Purchase a Jenova set", "Buy one three-piece legging set or short set from our shop."],
  ["Sign in", "Sign in with Google using the same email you used at checkout."],
  ["Apply", "Tell us about yourself and what you'd love to experience."],
  ["Get approved", "Our founder personally reviews every application."],
];

function NeedsSet() {
  return (
    <div data-testid="apply-needs-set-panel">
      <p className="jc-display italic text-3xl text-[#A8865A]">First, find your set.</p>
      <p className="mt-4 text-[#4A403B] leading-relaxed">{SET_REQUIREMENT} Use this same email at checkout and the application opens automatically.</p>
      <Link to={PRIVE_SHOP_URL} className="jc-btn jc-btn-primary mt-8" data-testid="apply-shop-sets-btn">Shop qualifying sets</Link>
    </div>
  );
}

function Toggle({ items, value, onChange, prefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((i) => {
        const on = value.includes(i);
        return (
          <button type="button" key={i} className="jc-chip" data-active={on} data-testid={`${prefix}-${i.toLowerCase().replace(/\W+/g, "-")}`}
            onClick={() => onChange(on ? value.filter((x) => x !== i) : [...value, i])}>
            {i}
          </button>
        );
      })}
    </div>
  );
}

function ApplicationForm({ user, onDone }) {
  const [first, ...rest] = (user.name || "").split(" ");
  const [f, setF] = useState({ first_name: first || "", last_name: rest.join(" "), email: user.email, city: "", state: "", instagram: "",
    introduction: "", interests: [], activities: [], agree_guidelines: false, agree_privacy: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agree_guidelines || !f.agree_privacy) return toast.error("Please agree to the community guidelines and privacy policy");
    setBusy(true);
    try {
      await cApi.post("/community/applications", f);
      toast.success("Application received — check your inbox");
      onDone();
      document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, props = {}) => (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] text-[#7A6A62]">{label}</span>
      <input className="jc-input" value={f[k]} onChange={set(k)} data-testid={`apply-${k}-input`} {...props} />
    </label>
  );

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="apply-form">
      <div className="grid sm:grid-cols-2 gap-8">
        {field("first_name", "First name", { required: true })}
        {field("last_name", "Last name", { required: true })}
        {field("email", "Email address", { required: true, type: "email" })}
        {field("instagram", "Instagram (optional)", { placeholder: "@yourname" })}
        {field("city", "City", { required: true })}
        {field("state", "State", { required: true })}
      </div>
      <label className="block">
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#7A6A62]">A short introduction</span>
        <textarea className="jc-input min-h-[120px] resize-y" required minLength={10} maxLength={1500} value={f.introduction} onChange={set("introduction")}
          placeholder="Who you are, what brings you to Jenova, what you hope to find…" data-testid="apply-introduction-input" />
      </label>
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A6A62] mb-4">Your interests</p>
        <Toggle items={INTERESTS} value={f.interests} onChange={set("interests")} prefix="apply-interest" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A6A62] mb-4">Activities you'd love to join</p>
        <Toggle items={ACTIVITIES} value={f.activities} onChange={set("activities")} prefix="apply-activity" />
      </div>
      <div className="space-y-3 text-sm text-[#4A403B]">
        <label className="flex items-start gap-3"><input type="checkbox" className="mt-1 accent-[#2B2522]" checked={f.agree_guidelines} onChange={set("agree_guidelines")} data-testid="apply-agree-guidelines" />
          <span>I agree to the <Link to="/community/guidelines" target="_blank" className="underline underline-offset-4">community guidelines</Link>.</span></label>
        <label className="flex items-start gap-3"><input type="checkbox" className="mt-1 accent-[#2B2522]" checked={f.agree_privacy} onChange={set("agree_privacy")} data-testid="apply-agree-privacy" />
          <span>I agree to the <Link to="/community/guidelines#privacy" target="_blank" className="underline underline-offset-4">privacy policy</Link> and to receive community emails (you can manage these anytime).</span></label>
      </div>
      <button type="submit" disabled={busy} className="jc-btn jc-btn-primary" data-testid="apply-submit-btn">
        {busy ? "Sending…" : "Submit my application"} <ArrowRight className="w-4 h-4" strokeWidth={1.25} />
      </button>
    </form>
  );
}

function ApplyPanel() {
  const { user, me, loading, reload } = useCommunityMe();
  if (loading) return <div className="h-40" />;
  if (!user)
    return (
      <div data-testid="apply-signin-panel">
        <p className="text-[#4A403B] leading-relaxed" data-testid="apply-requirement">{SET_REQUIREMENT}</p>
        <p className="mt-4 text-[#4A403B] leading-relaxed">Already have your set? Sign in with the email you used at checkout to apply.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => startGoogleLogin("/become-a-jenova-girl")} className="jc-btn jc-btn-primary" data-testid="apply-signin-btn">Sign in with Google to apply</button>
          <Link to={PRIVE_SHOP_URL} className="jc-btn jc-btn-ghost" data-testid="apply-signin-shop-btn">Shop qualifying sets</Link>
        </div>
      </div>
    );
  const status = me?.membership?.community_status;
  if (status && status !== "none" && status !== "rejected")
    return (
      <div data-testid="apply-status-panel">
        <p className="jc-display italic text-3xl text-[#A8865A]">
          {status === "pending" ? "Your application is being reviewed." : status === "approved" ? "You're a Jenova Girl." : "Your membership is paused."}
        </p>
        <p className="mt-4 text-sm text-[#7A6A62]">{status === "pending" ? "We'll email you as soon as our founder has reviewed it." : "Visit My Jenova for your RSVPs and preferences."}</p>
        <Link to="/my-jenova" className="jc-btn jc-btn-ghost mt-8" data-testid="apply-my-jenova-btn">Open My Jenova</Link>
      </div>
    );
  if (!me?.has_qualifying_purchase) return <NeedsSet />;
  return <ApplicationForm user={user} onDone={reload} />;
}

export default function BecomeJenovaGirl() {
  return (
    <CommunityLayout>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-20 grid md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-7">
          <p className="jc-eyebrow jc-fade jc-d1">Become a Jenova Girl</p>
          <h1 className="jc-display text-5xl sm:text-6xl lg:text-7xl mt-5 jc-fade jc-d2" data-testid="join-title">
            A sisterhood for women who are <em className="italic text-[#A8865A]">becoming.</em>
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#4A403B] max-w-xl leading-relaxed jc-fade jc-d3">
            Our mission is to create a space where women grow in confidence, wellness and faith — and never have to do it alone.
          </p>
        </div>
        <div className="md:col-span-5 jc-card-img aspect-[4/5] jc-fade jc-d3"><img src={STOCK.sisterhood} alt="" className="w-full h-full object-cover" /></div>
      </section>

      <section className="bg-[#F3E1DC]/60">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24">
          <SectionTitle eyebrow="What Jenova stands for" title="Five pillars," em="one heart" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {PILLARS.map(([t, b], i) => (
              <div key={t} className="border-t border-[#C9A97A] pt-6">
                <p className="font-serif text-sm text-[#A8865A]">0{i + 1}</p>
                <h3 className="jc-display text-3xl mt-2">{t}</h3>
                <p className="mt-3 text-sm text-[#7A6A62] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <SectionTitle eyebrow="What it means" title="Being a" em="Jenova Girl">
            It means being part of a circle of women who choose kindness, wellness and growth — and who invite each other into it.
            Members are invited to wellness days, girls' brunches, sip & paint nights, photoshoots, beauty days, Bible studies,
            networking evenings, travel experiences and community gatherings.
          </SectionTitle>
          <div className="mt-12 space-y-6">
            {STEPS.map(([t, b], i) => (
              <div key={t} className="flex gap-5">
                <span className="w-9 h-9 shrink-0 rounded-full border border-[#C9A97A] flex items-center justify-center font-serif text-[#A8865A]">{i + 1}</span>
                <div><p className="font-serif text-xl">{t}</p><p className="text-sm text-[#7A6A62] mt-1">{b}</p></div>
              </div>
            ))}
          </div>
          <p className="mt-12 text-sm text-[#4A403B] leading-relaxed inline-flex gap-2" data-testid="join-requirement">
            <Check className="w-4 h-4 text-[#A8865A] shrink-0 mt-0.5" strokeWidth={1.25} />
            {SET_REQUIREMENT}
          </p>
        </div>
        <div className="md:col-span-6 md:col-start-7 bg-[#FBF8F3] border border-[#E8DCCB] p-8 md:p-12 rounded-sm" id="apply">
          <p className="jc-eyebrow">The application</p>
          <h2 className="jc-display text-4xl mt-3 mb-10">Tell us about <em className="italic">you</em></h2>
          <ApplyPanel />
        </div>
      </section>
    </CommunityLayout>
  );
}
